import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Empty, CopyButton } from "@/components/shared";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Wallet() {
  const [wallets, setWallets] = useState([]);
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/merchant/wallets").then((r) => setWallets(r.data));
  useEffect(() => { load(); }, []);

  const doPreview = async () => {
    setBusy(true);
    try { const { data } = await api.post("/merchant/wallets/preview", { extended_key: key.trim() }); setPreview(data); }
    catch (e) { setPreview(null); toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try { await api.post("/merchant/wallets", { extended_key: key.trim(), label }); toast.success("Wallet enregistré (chiffré AES-256-GCM)"); setKey(""); setLabel(""); setPreview(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Wallet (zpub / vpub)" subtitle="Collez la clé publique étendue de votre wallet BIP84 (BlueWallet, Sparrow, Electrum…). zpub/xpub = mainnet (mode live), vpub/tpub = testnet (mode test). Aucune clé privée n'est jamais demandée." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="panel p-5 lg:col-span-2 space-y-4">
          <Textarea rows={3} placeholder="zpub6r… ou vpub5Y…" value={key} onChange={(e) => { setKey(e.target.value); setPreview(null); }} className="font-mono text-xs" data-testid="zpub-input-field" />
          <Input placeholder="Libellé (ex : BlueWallet boutique)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="zpub-label-input" />
          <div className="flex gap-2">
            <Button variant="secondary" onClick={doPreview} disabled={!key || busy} data-testid="zpub-preview-button">Vérifier & prévisualiser</Button>
            <Button onClick={save} disabled={!preview || busy} className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold" data-testid="zpub-save-button">Enregistrer / Rotation</Button>
          </div>
          {preview && (
            <div className="border border-zinc-800 rounded-lg p-4 space-y-3 fade-up" data-testid="derived-addresses-preview-list">
              <div className="flex items-center justify-between text-xs">
                <span className="eyebrow">Réseau : <b className={preview.network === "mainnet" ? "text-emerald-400" : "text-amber-400"}>{preview.network}</b> · {preview.prefix} → xpub {preview.xpub_preview}</span>
                <span className="font-mono text-zinc-500">fp {preview.fingerprint}</span>
              </div>
              <p className="text-xs text-zinc-400">Comparez ces 3 premières adresses avec celles affichées dans votre wallet (BlueWallet → Recevoir / Sparrow → Addresses).</p>
              {preview.addresses.map((a) => (
                <div key={a.index} className="mono-box flex items-center justify-between gap-2">
                  <div><div className="text-[10px] text-zinc-500" data-testid="address-derivation-path-label">{a.path}</div><div data-testid="preview-address">{a.address}</div></div>
                  <CopyButton value={a.address} label="Adresse copiée" testId={`copy-preview-${a.index}`} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-4">
          <div className="eyebrow">Wallets enregistrés</div>
          {wallets.length === 0 ? <Empty text="Aucun wallet." /> : wallets.map((w) => (
            <div key={w.id} className={`panel p-4 text-sm ${w.active ? "border-amber-500/30" : "opacity-60"}`} data-testid="wallet-row">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{w.label || w.prefix}</span>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${w.network === "mainnet" ? "text-emerald-400 border-emerald-500/30" : "text-amber-400 border-amber-500/30"}`}>{w.network}</span>
              </div>
              <div className="text-xs text-zinc-500 mt-1">{w.active ? "actif" : `remplacé le ${fmtDate(w.rotated_at)}`} · index suivant : {w.next_index} · fp {w.fingerprint}</div>
              <div className="text-xs text-zinc-500">{w.unused_addresses} adresse(s) en attente {w.gap_limit_warning && <span className="text-red-400 font-medium" data-testid="gap-warning">· gap limit dépassé !</span>}</div>
              <div className="mt-2 text-[10px] font-mono text-zinc-600 break-all">{w.preview?.[0]?.address}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
